'use strict';
/*
  player.js — the browser frame loop. A direct port of lib/engine.js's
  makeState/step/drawFrame (which are pure JS), with the ffmpeg pipe replaced by
  drawing onto a visible <canvas> and capturing it with MediaRecorder.

  Talk mode: the uploaded <video> is drawn first (object-fit: cover into 9:16),
  then the style's transparent caption layer is composited on top — exactly what
  the Node `renderOverlay` path produces, but live in the browser.
*/
import { createToolkit, rng, clamp } from './draw.browser.js';

function makeState(W, H, fps, style, analysis, cues, mode, opts) {
  const tk = createToolkit(W, H);
  const st = {
    W, H, CX: W / 2, CY: H / 2, fps, mode, tk, opts: opts || {},
    overlayMode: mode === 'talk' || mode === 'photos',
    reactive: mode !== 'talk',
    t: 0, NOW: 0, f: 0,
    bass: analysis.bass || [], mid: analysis.mid || [], dur: analysis.dur, spectrum: analysis.spectrum || null,
    b: 0, m: 0, beatAvg: 18, lastBeat: -1e9,
    shake: 0, flash: 0, flashColor: style.palette.accent || '#ff1717',
    punch: 0, energy: 0, hue: 0, kick: 0,
    cues, cueIndex: -1, cue: null, cueAge: 0, prevCue: null, prevCueLeftAt: -1e9,
    progress: 0, rnd: rng(0x9e3779b1), palette: style.palette, s: {},
  };
  if (style.init) style.init(st);
  return st;
}

function step(st, style) {
  const { fps } = st;
  st.t = st.f / fps; st.NOW = st.t * 1000;
  st.b = st.bass[st.f] || 0; st.m = st.mid[st.f] || 0;
  st.progress = st.dur ? clamp(st.t / st.dur, 0, 1) : 0;
  st.energy = st.energy * 0.85 + (st.b / 255) * 0.15;
  st.hue = (st.hue + 0.6 + st.energy * 2.2) % 360;
  st.beatAvg = st.beatAvg * 0.94 + st.b * 0.06; st.kick = 0;
  if (st.reactive && st.b > st.beatAvg * 1.35 && st.b > 60 && st.NOW - st.lastBeat > 160) {
    st.lastBeat = st.NOW; const power = Math.min(1.6, st.b / 110);
    st.kick = power; st.shake = Math.min(38, st.shake + 16 * power);
    st.flash = Math.max(st.flash, Math.min(0.5, 0.3 * power)); st.punch = Math.min(0.10, st.punch + 0.055 * power);
    if (style.onBeat) style.onBeat(st, power);
  }
  st.shake *= 0.82; st.flash *= 0.8; st.punch *= 0.80;
  let idx = -1;
  for (let i = 0; i < st.cues.length; i++) { if (st.t >= st.cues[i].t) idx = i; else break; }
  if (idx !== st.cueIndex && idx >= 0) {
    st.prevCue = st.cueIndex >= 0 ? st.cues[st.cueIndex] : null; st.prevCueLeftAt = st.t;
    st.cueIndex = idx; st.cue = st.cues[idx];
    if (style.onCue) style.onCue(st, st.cue);
    st.shake = Math.min(38, st.shake + (st.reactive ? 8 : 2));
    st.punch = Math.min(0.10, st.punch + (st.reactive ? 0.03 : 0.018));
  }
  st.cue = st.cueIndex >= 0 ? st.cues[st.cueIndex] : null;
  st.cueAge = st.cue ? st.t - st.cue.t : 0;
  st.exitAge = st.t - st.prevCueLeftAt;
}

function cover(ctx, img, W, H) {
  const iw = img.videoWidth || img.width, ih = img.videoHeight || img.height;
  if (!iw || !ih) return;
  const s = Math.max(W / iw, H / ih), dw = iw * s, dh = ih * s;
  ctx.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);
}

function drawFrame(ctx, st, style, video) {
  const { W, H } = st;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  const sh = st.shake, dx = (st.rnd() - 0.5) * sh, dy = (st.rnd() - 0.5) * sh, rot = (st.rnd() - 0.5) * sh * 0.0012, pz = 1 + st.punch;
  ctx.save();
  ctx.translate(W / 2 + dx, H / 2 + dy); ctx.scale(pz, pz); ctx.rotate(rot); ctx.translate(-W / 2, -H / 2);
  if (st.overlayMode && video) { ctx.clearRect(0, 0, W, H); cover(ctx, video, W, H); }
  else { ctx.fillStyle = style.palette.bg || '#000'; ctx.fillRect(0, 0, W, H); }
  if (style.background) style.background(ctx, st);
  if (style.drawCue && st.cue) style.drawCue(ctx, st);
  ctx.restore();
  if (style.overlay) style.overlay(ctx, st);
}

function pickMime() {
  const c = ['video/mp4;codecs=avc1.42E01E,mp4a.40.2', 'video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'];
  for (const m of c) if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) return m;
  return '';
}

export class Player {
  constructor({ canvas, video, style, cues, fps = 30, mode = 'talk', dur, watermark, dub = null }) {
    this.canvas = canvas; this.video = video; this.style = style; this.fps = fps;
    this.dub = dub; // { audioCtx, buffers:[{buffer, start}] } — replaces the original voice
    this.dur = dur || (video ? video.duration : 0);
    this.ctx = canvas.getContext('2d');
    this.st = makeState(canvas.width, canvas.height, fps, style, { bass: [], mid: [], dur: this.dur, spectrum: null }, cues, mode, { watermark });
  }
  drawAt(t) { const st = this.st; st.f = Math.round(t * this.fps); step(st, this.style); drawFrame(this.ctx, st, this.style, this.video); }
  preview(t) { this.drawAt(t || 0); }

  async record(onProgress) {
    const { canvas, video, fps, dur, dub } = this;
    const stream = canvas.captureStream(fps);
    let audioCtx = null, ownsCtx = false;
    if (dub && dub.buffers && dub.buffers.length) {
      // DUB: replace the original voice with the synthetic track — schedule each
      // segment's clip at its caption start time on a WebAudio timeline.
      try {
        audioCtx = dub.audioCtx; await audioCtx.resume();
        const dest = audioCtx.createMediaStreamDestination();
        const lead = audioCtx.currentTime + 0.15; // small head-start so scheduling lands
        for (const b of dub.buffers) {
          if (!b || !b.buffer) continue;
          const s = audioCtx.createBufferSource(); s.buffer = b.buffer; s.connect(dest);
          s.start(lead + Math.max(0, b.start || 0));
        }
        dest.stream.getAudioTracks().forEach(t => stream.addTrack(t));
        if (video) video.muted = true; // never capture/play the original voice when dubbing
      } catch (e) { console.warn('dub audio unavailable:', e.message); }
    } else if (video) {
      try {
        video.muted = false; // (a previous dubbed render may have muted it)
        // A MediaElementSource can be created ONLY ONCE per element for its lifetime.
        // Cache it (and its context) on the element so re-renders — e.g. after changing
        // the caption style — still capture the audio instead of throwing & going silent.
        let g = video._mesGraph;
        if (!g) {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          g = video._mesGraph = { ctx, src: ctx.createMediaElementSource(video) };
        }
        audioCtx = g.ctx; await audioCtx.resume();
        const dest = audioCtx.createMediaStreamDestination();
        try { g.src.disconnect(); } catch (e) {}
        g.src.connect(dest);
        dest.stream.getAudioTracks().forEach(t => stream.addTrack(t));
      } catch (e) { console.warn('audio capture unavailable:', e.message); }
    }
    const mime = pickMime();
    const rec = new MediaRecorder(stream, Object.assign({ videoBitsPerSecond: 10000000 }, mime ? { mimeType: mime } : {}));
    const chunks = []; rec.ondataavailable = e => { if (e.data && e.data.size) chunks.push(e.data); };
    const stopped = new Promise(r => (rec.onstop = r));
    if (video) { video.currentTime = 0; try { await video.play(); } catch (e) {} }
    rec.start(100);
    await new Promise(res => {
      const t0 = performance.now();
      const loop = () => {
        const wall = (performance.now() - t0) / 1000;
        const t = video ? video.currentTime : wall;
        // Finish on video-end / target time — OR a wall-clock failsafe: a real-time
        // render can't take longer than the clip itself (+ a margin), so this guards
        // against a stalled/paused video that never reaches `dur`.
        if ((video && (video.ended || t >= dur - 0.03)) || (!video && t >= dur) || wall >= dur + 1.5) { res(); return; }
        this.drawAt(t); onProgress && onProgress(Math.min(1, t / dur));
        requestAnimationFrame(loop);
      };
      requestAnimationFrame(loop);
    });
    try { rec.requestData(); } catch (e) {}
    rec.stop(); if (video) video.pause();
    // Wait for the recorder to FULLY finalize (the moov atom etc.). Do NOT race a
    // short timeout here — cutting this off produces a truncated, unplayable file.
    // The infinite-hang guard is the wall-clock cap in the loop above, not here.
    await stopped;
    // only close a context WE created; the dub context is owned/closed by the caller
    if (audioCtx && ownsCtx) try { await audioCtx.close(); } catch (e) {}
    return new Blob(chunks, { type: (mime || 'video/webm').split(';')[0] });
  }
}
